import type { AxiosError } from 'axios'
import throttle from 'lodash/throttle'
import React from 'react'
import * as z from 'zod'

import type { SupportedMarketKey } from '@nxt-insurance/constants'

import type { Option } from '@ui-kit/components/formComponents/Select/types'

export type AddressDetail = {
  street: string
  houseNumber: string
  city: string
  country: string
  postalCode: string
  url: string
  lat: number
  lng: number
  image: string
}

export type Service = {
  get: <T>(endpoint: string) => Promise<{ data: T }>
}

export type Status =
  | {
      name: 'idle'
    }
  | {
      name: 'loading'
    }
  | {
      name: 'error'
      error_message: string
      status: string
    }
  | {
      name: 'success'
      predictions: Option[]
    }
  | {
      name: 'addressDetailLoaded'
    }

const predictionsSchema = z.object({
  predictions: z.array(
    z.object({
      description: z.string(),
      place_id: z.string(),
      types: z.array(z.string()),
    })
  ),
})

const errorSchema = z.object({
  error_message: z.string(),
  status: z.string(),
})

type Prediction = z.infer<typeof predictionsSchema>['predictions'][number]

type Error = z.infer<typeof errorSchema>

const THROTTLE_INTERVAL = 600

export const useAddressAutocomplete = (service: Service) => {
  const [status, setStatus] = React.useState<Status>({ name: 'idle' })

  const loadPredictions = React.useMemo(
    () =>
      throttle(async (input: string, country: SupportedMarketKey) => {
        if (status.name === 'loading' || !input) {
          return
        }
        if (status.name !== 'success') {
          setStatus({ name: 'loading' })
        }
        try {
          const result = await service.get<{ predictions: Prediction[] } | Error>(
            `v1/address_autocomplete?input=${input}&country=${country}`
          )
          const predictionsResult = predictionsSchema.safeParse(result.data)
          if (predictionsResult.success) {
            setStatus({
              name: 'success',
              predictions: predictionsResult.data.predictions.map((prediction) => {
                return {
                  label: prediction.description,
                  value: prediction.place_id,
                }
              }),
            })
          }
          const errorResult = errorSchema.safeParse(result.data)
          if (errorResult.success) {
            setStatus({
              name: 'error',
              error_message: errorResult.data.error_message,
              status: errorResult.data.status,
            })
          }
        } catch (error) {
          const networkError = error as AxiosError
          setStatus({
            name: 'error',
            error_message: networkError.message,
            status: networkError.status ? networkError.status.toString() : '',
          })
        }
      }, THROTTLE_INTERVAL),
    [service, status]
  )

  const loadAddressDetail = React.useCallback(
    async (
      placeId: string,
      dimensions?: { width: string; height: string }
    ): Promise<AddressDetail | null> => {
      setStatus({ name: 'loading' })
      try {
        const result = await service.get<{ result: Omit<AddressDetail, 'image'> } | Error>(
          `v1/address_autocomplete/details?placeid=${placeId}`
        )
        if ('result' in result.data) {
          const imgResult = await service.get<any>(
            `v1/address_autocomplete/image?lat=${result.data.result.lat}&lng=${result.data.result.lng}`.concat(
              dimensions ? `&width=${dimensions.width}&height=${dimensions.height}` : ''
            )
          )
          setStatus({ name: 'addressDetailLoaded' })
          return { ...result.data.result, image: imgResult.data.image }
        }
        setStatus({
          name: 'error',
          error_message: result.data.error_message,
          status: result.data.status,
        })
        return null
      } catch (error) {
        setStatus({
          name: 'error',
          error_message: `An unknown error occurred: ${error}.`,
          status: '',
        })
        return null
      }
    },
    [service]
  )

  return {
    status,
    loadPredictions,
    loadAddressDetail,
  }
}
